import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast, showSuccessToast } from "#app/notifications";
import {
  downloadExportQuote,
  ExportQuoteValidationError,
} from "#modules/quoteRequests/infra/exportQuote.api";

function isValidationError(err: unknown): err is ExportQuoteValidationError {
  return (
    typeof err === "object" &&
    err !== null &&
    "errors" in err &&
    Array.isArray((err as ExportQuoteValidationError).errors) &&
    "warnings" in err &&
    Array.isArray((err as ExportQuoteValidationError).warnings)
  );
}

export const useExportQuote = (options?: {
  onSuccess?: () => void;
  onError?: (err: Error) => void;
}) => {
  const { t } = useTranslation();

  const { mutate, ...rest } = useMutation({
    mutationFn: downloadExportQuote,
    onSuccess: () => {
      showSuccessToast({
        title: t("quoteRequestLine.actions.exportQuote.success"),
      });

      options?.onSuccess?.();
    },
    onError: (err: unknown) => {
      // Handle validation errors with structured error messages
      if (isValidationError(err)) {
        const errorMessages = err.errors.map((e) => e.message).join("\n");
        showErrorToast({
          title: t("quoteRequestLine.actions.exportQuote.validationError"),
          message: errorMessages,
        });
      } else if (err instanceof Error) {
        showErrorToast({ title: err.message });
        errorHandler(err);
      } else {
        showErrorToast({
          title: t("quoteRequestLine.actions.exportQuote.unknownError"),
        });
      }

      options?.onError?.(err instanceof Error ? err : new Error(String(err)));
    },
  });

  return { exportQuote: mutate, ...rest };
};
